import React, { useEffect, useState, useCallback } from "react";
import { observer } from "mobx-react-lite";
import { CContainer, CCard, CCardBody } from "@coreui/react";
import settingsStore from "../../store/SettingsStore";
import periodPrefsStore from "../../store/PeriodPrefsStore";
import PeriodFilters from "../UI/PeriodFilters/PeriodFilters";
import ServerSelect from "../UI/PeriodFilters/ServerSelect";
import ErrorAlert from "../UI/Status/ErrorAlert";
import { SkeletonCard } from "../UI/Skeleton/Skeleton";
import { getPromotionsReport } from "../../api/reportsApi";
import { PromotionsReportView } from "../Assistant/ToolResultView";
import { logReportView } from "../../api/reportLogApi";
import { formatYmd } from "../../utils/dateRange";

const PromotionsPage = observer(() => {
  const [selectedServer, setSelectedServer] = useState(periodPrefsStore.selectedServer);
  const [fromDate, setFromDate] = useState(periodPrefsStore.fromDate);
  const [toDate, setToDate] = useState(periodPrefsStore.toDate);
  const [quickRange, setQuickRange] = useState(periodPrefsStore.quickRange);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(
    async (from = fromDate, to = toDate) => {
      if (!selectedServer || !from || !to) return;
      setLoading(true);
      setError(null);
      try {
        const data = await getPromotionsReport(selectedServer, from, to);
        setResult(data);
      } catch (e) {
        setError(e);
      } finally {
        setLoading(false);
      }
    },
    [selectedServer, fromDate, toDate],
  );

  useEffect(() => {
    const init = async () => {
      const userId = localStorage.getItem("userId");
      if (userId) await settingsStore.getServers(userId);
      if (!fromDate || !toDate) {
        const today = new Date();
        const past = new Date(today);
        past.setDate(today.getDate() - 14);
        setFromDate(formatYmd(past));
        setToDate(formatYmd(today));
      }
      logReportView({ event: "report_view", reportKey: "promotions", reportTitle: "Акции и скидки", userId, path: "/promotions" });
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!settingsStore.servers?.length) return;
    if (!selectedServer) setSelectedServer(String(settingsStore.servers[0].id));
  }, [settingsStore.servers, selectedServer]);

  useEffect(() => {
    if (selectedServer) periodPrefsStore.setServer(selectedServer);
  }, [selectedServer]);

  useEffect(() => {
    if (selectedServer && fromDate && toDate) load(fromDate, toDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedServer, fromDate, toDate]);

  const handleRefresh = (from, to) => {
    periodPrefsStore.setPeriod(from, to, quickRange);
    load(from, to);
  };

  return (
    <CContainer style={{ flex: "1", marginTop: "120px" }}>
      <h4 className="mb-3">Акции и скидки</h4>
      <p className="text-body-secondary small">
        Разбивка по названию акции/скидки (как она называется в iiko) за период: сколько раз применялась, сумма скидки,
        средний % и доля в общих скидках — для оценки эффективности и выгодности каждой акции.
      </p>
      <ServerSelect servers={settingsStore.servers} value={selectedServer} onChange={(e) => setSelectedServer(e.target.value)} />
      <PeriodFilters
        fromDate={fromDate}
        toDate={toDate}
        onFromChange={setFromDate}
        onToChange={setToDate}
        onRefresh={handleRefresh}
        quickRange={quickRange}
        onQuickRangeChange={setQuickRange}
        disabled={loading}
        loading={loading}
      />
      <ErrorAlert error={error} />
      <CCard className="mt-3">
        <CCardBody>
          {loading && !result ? <SkeletonCard lines={4} /> : result ? <PromotionsReportView result={result} /> : null}
        </CCardBody>
      </CCard>
    </CContainer>
  );
});

export default PromotionsPage;
